import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Clock, BadgeCheck, Lock } from "lucide-react";
import api from "../../lib/api";

/* Trial/membership status banner shown at the top of My Travel Hub.
   Reminder stages: welcome (60-31d), halfway (30-11d), urgent (10-4d), final (3-1d),
   expired (read-only — photos/documents/notes still allowed). */

export default function MembershipBanner({ tripCount }) {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/memberships/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  if (!me) return null;

  if (me.status === "active" || me.status === "complimentary") {
    return (
      <div className="bg-emerald-50 border border-emerald-300 px-4 py-2.5 flex flex-wrap items-center gap-2 text-sm" data-testid="membership-banner-active">
        <BadgeCheck className="w-4 h-4 text-emerald-700" />
        <span className="text-emerald-900">
          {me.status === "complimentary"
            ? <>Complimentary <b>Individual</b> membership — thank you for supporting TravelerPulse!</>
            : <><b>{me.plan_label}</b> member{me.seats > 1 ? ` · ${me.seats} traveler profiles` : ""}{me.founding ? " · ⭐ Founding Member" : ""}</>}
          {(me.paid_until || me.complimentary_until) && <> · valid until {new Date(me.paid_until || me.complimentary_until).toLocaleDateString()}</>}
        </span>
        {me.supporter_badge && <span className="ml-auto inline-flex items-center gap-1 text-amber-700 font-700"><BadgeCheck className="w-4 h-4" /> Founding Supporter — {me.supporter_badge}</span>}
      </div>
    );
  }

  if (me.status === "expired") {
    return (
      <div className="bg-rose-50 border border-rose-300 px-4 py-3 text-sm" data-testid="membership-banner-expired">
        <div className="flex items-start gap-2">
          <Lock className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
          <div>
            <span className="font-700 text-rose-800">Your free trial has ended — My Travel Hub is now read-only.</span>
            <span className="text-rose-900"> Your trips and data are safe, and you can still add photos, documents and notes. Subscribe to keep planning new trips.</span>
            <Link to="/membership" data-testid="banner-subscribe" className="ml-2 font-700 text-indigo-700 hover:underline">See plans from $19.99/year →</Link>
          </div>
        </div>
      </div>
    );
  }

  // trial
  const d = me.trial_days_left;
  const stage = d > 22
    ? { cls: "bg-emerald-50 border-emerald-300 text-emerald-900", msg: <>Your <b>45-day free trial</b> is active — <b>{d} days left</b>.{tripCount === 0 ? " Start by creating your first trip below!" : " Keep building your journeys."}</> }
    : d > 7
      ? { cls: "bg-sky-50 border-sky-300 text-sky-900", msg: <>You're over halfway through your free trial — <b>{d} days left</b>. Enjoying your Hub?</> }
      : d > 3
        ? { cls: "bg-amber-50 border-amber-300 text-amber-900", msg: <>Only <b>{d} days left</b> in your free trial. Keep your Hub active from just $39 a year.</> }
        : { cls: "bg-orange-50 border-orange-400 text-orange-900", msg: <><b>{d === 0 ? "Your trial ends today" : `${d} day${d === 1 ? "" : "s"} remaining`}</b> — subscribe to keep planning trips (your data stays safe either way).</> };

  return (
    <div className={`border px-4 py-2.5 flex flex-wrap items-center gap-2 text-sm ${stage.cls}`} data-testid="membership-banner-trial">
      {d > 30 ? <Sparkles className="w-4 h-4 shrink-0" /> : <Clock className="w-4 h-4 shrink-0" />}
      <span>{stage.msg}</span>
      <Link to="/membership" data-testid="banner-plans" className="ml-auto font-700 text-indigo-700 hover:underline whitespace-nowrap">See membership plans →</Link>
    </div>
  );
}
